import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import NewsDetailPage from './pages/NewsDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CreateNewsPage from './pages/CreateNewsPage';
import EditNewsPage from './pages/EditNewsPage';
import AdminPage from './pages/AdminPage';
import InstallPage from './pages/InstallPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CredentialPage from './pages/CredentialPage';
import ProfilePage from './pages/ProfilePage';
import DenunciaPage from './pages/DenunciaPage';
import ArchivePage from './pages/ArchivePage';
import VerifyCredentialPage from './pages/VerifyCredentialPage';
import { Loader2 } from 'lucide-react';

/* Error Boundary - prevents blank pages */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App Error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#0B1F40',color:'white',fontFamily:'sans-serif',textAlign:'center',padding:'2rem'}}>
          <div>
            <h1 style={{color:'#E50B15',fontSize:'2rem',fontWeight:900,marginBottom:'0.5rem'}}>RP NOTICIAS</h1>
            <p style={{color:'rgba(255,255,255,0.5)',marginBottom:'1.5rem'}}>Ocurrió un error al cargar la página</p>
            <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }}
              style={{background:'#E50B15',color:'white',border:'none',padding:'12px 32px',fontWeight:900,cursor:'pointer',fontSize:'14px'}}>
              RECARGAR
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#E50B15]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <Header />
      <main className="min-h-[calc(100vh-64px)]">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/news/:id" element={<NewsDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<ProtectedRoute adminOnly><RegisterPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/create-news" element={<ProtectedRoute><CreateNewsPage /></ProtectedRoute>} />
          <Route path="/edit-news/:id" element={<ProtectedRoute><EditNewsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/credential/:id" element={<ProtectedRoute adminOnly><CredentialPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/denuncia" element={<DenunciaPage />} />
          <Route path="/archivo" element={<ArchivePage />} />
          <Route path="/verificar" element={<VerifyCredentialPage />} />
          <Route path="/verificar/:code" element={<VerifyCredentialPage />} />
          <Route path="/descargar" element={<InstallPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </main>
      <footer className="border-t-4 border-[#0B1F40] bg-[#0B1F40] py-6 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[10px] font-black tracking-[0.2em] uppercase text-white/30">
            <span className="text-[#E50B15]">RP</span> NOTICIAS {new Date().getFullYear()}
          </p>
          <p className="text-[10px] text-white/15 italic tracking-wider">La crítica de los hechos</p>
        </div>
      </footer>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
